import axios from "axios";

/**
 * Axios client with automatic 401 → refresh → retry handling.
 *
 * Why this exists: access tokens are short-lived (1d server-side). On
 * expiry, every authenticated request would 401 silently and the UI
 * would show empty states instead of redirecting to login. This
 * interceptor catches the first 401 per request, refreshes via the
 * stored refresh token, updates localStorage, and retries the original
 * request transparently.
 *
 * If the refresh ALSO fails (refresh token expired or revoked server-
 * side), we clear auth state and hard-redirect to /auth — the user
 * has to log in again. That's the right end-state: better a forced
 * login than a UI silently broken.
 *
 * Concurrency: while a refresh is in flight, any other 401s that come
 * in queue up on the same refresh promise rather than each kicking off
 * their own. After the refresh resolves (success or fail) all queued
 * requests retry with the new token (or reject if the refresh failed).
 */

const STORAGE_KEY = "astrologerProfile";

/* ─── Auth storage helpers ────────────────────────────────────── */

function readProfile() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeTokens({ accessToken, refreshToken }) {
  const p = readProfile();
  if (!p) return;
  if (accessToken) p.accessToken = accessToken;
  if (refreshToken) p.refreshToken = refreshToken;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
}

function clearAuth() {
  localStorage.removeItem(STORAGE_KEY);
}

/* ─── Axios instance ──────────────────────────────────────────── */

export const client = axios.create({
  baseURL: `${import.meta.env.VITE_BACKEND_URL}`,
});

/* ─── Request interceptor (always use storage token) ──────────── */

// Callers (e.g. api/services.js) often pass an Authorization header
// built from a stale in-memory `useAuth().astrologer.accessToken`. The
// interceptor below refreshes tokens in localStorage on 401, but the
// in-memory snapshot isn't updated until next mount, so without this
// override every subsequent request 401s, triggers a refresh, and
// retries. Replacing the header with the storage value on every
// outgoing request avoids that storm.
client.interceptors.request.use((config) => {
  try {
    const profile = readProfile();
    if (profile?.accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${profile.accessToken}`;
    }
  } catch {
    // localStorage hiccup — let the request go as-is.
  }
  return config;
});

/* ─── Refresh-in-flight singleton ─────────────────────────────── */

let refreshPromise = null;

async function refreshTokens() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const profile = readProfile();
    const refreshToken = profile?.refreshToken;
    if (!refreshToken) {
      throw new Error("No refresh token in storage");
    }

    // Use a bare axios call — don't go through `client`, otherwise the
    // interceptor would catch our own refresh request's 401 and loop.
    const { data } = await axios.post(
      `${import.meta.env.VITE_BACKEND_URL}/api/astrologer/refresh-token`,
      { refreshToken }
    );

    const newAccessToken = data?.data?.accessToken;
    const newRefreshToken = data?.data?.refreshToken;
    if (!newAccessToken) {
      throw new Error("Refresh response missing accessToken");
    }

    writeTokens({
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
    });
    return newAccessToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/* ─── Response interceptor (401 -> refresh -> retry) ──────────── */

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Only act on 401s that haven't already been retried.
    if (status !== 401 || !original || original._retried) {
      return Promise.reject(error);
    }

    // Don't try to refresh the refresh endpoint itself (avoid loops).
    const url = String(original.url || "");
    if (url.includes("/api/astrologer/refresh-token")) {
      return Promise.reject(error);
    }

    // Don't try to refresh login — a 401 there means wrong creds, not
    // a stale token.
    if (url.includes("/api/astrologer/login")) {
      return Promise.reject(error);
    }

    original._retried = true;

    try {
      const newAccessToken = await refreshTokens();
      // Update the Authorization header on the retried request.
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newAccessToken}`;
      return client(original);
    } catch (refreshErr) {
      // Refresh failed — the user is genuinely logged out. Clear local
      // auth state and bounce to the login page. Preserve the current
      // path as ?callback= so post-login can return them.
      clearAuth();
      const cb = encodeURIComponent(
        window.location.pathname + window.location.search
      );
      // Hard navigate — React-router's navigate isn't available here
      // (we're outside the component tree). The auth provider's load
      // effect will pick up the cleared storage and render the auth
      // routes anyway.
      window.location.href = `/auth?callback=${cb}`;
      return Promise.reject(error);
    }
  }
);

/* ─── Public helpers for AuthContext ──────────────────────────── */

// Exposed so AuthContext.login can stash both tokens after login,
// keeping the storage shape in one place.
export const authStorage = {
  read: readProfile,
  writeTokens,
  clear: clearAuth,
};
